import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';

const ProfileTab = () => {
  const { user, updateProfile: updateProfileContext } = useAuth();
  const { t } = useTranslation();
  
  // État pour le formulaire de profil
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    username: user?.username || '',
    profession: user?.profession || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    // Initialiser le formulaire de profil avec les données utilisateur
    if (user) {
      setProfileForm({
        name: user.name || '',
        email: user.email || '',
        username: user.username || '',
        profession: user.profession || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  }, [user]);

  // Fonction pour mettre à jour le profil
  const updateProfile = async (e) => {
    e.preventDefault();
    setProfileLoading(true);

    try {
      const updateData = {
        name: profileForm.name,
        email: profileForm.email,
        username: profileForm.username,
        profession: profileForm.profession
      };

      // Ajouter le mot de passe seulement si fourni
      if (profileForm.currentPassword && profileForm.newPassword) {
        if (profileForm.newPassword !== profileForm.confirmPassword) {
          notification.error({
            message: 'Erreur',
            description: 'Les nouveaux mots de passe ne correspondent pas',
            placement: 'topRight',
            style: {
              borderRadius: 12,
              boxShadow: '0 4px 24px rgba(255, 36, 36, 0.12)',
              background: 'linear-gradient(90deg, #ef4444 0%, #be185d 100%)',
              color: '#fff',
              fontWeight: 500,
            },
            duration: 2.5,
          });
          setProfileLoading(false);
          return;
        }
        updateData.currentPassword = profileForm.currentPassword;
        updateData.newPassword = profileForm.newPassword;
      }

      const result = await updateProfileContext(updateData);
      
      if (result.success) {
        notification.success({
          message: 'Succès',
          description: result.message || 'Profil mis à jour avec succès',
          placement: 'topRight',
          style: {
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(80, 36, 255, 0.12)',
            background: 'linear-gradient(90deg, #6366f1 0%, #a21caf 100%)',
            color: '#fff',
            fontWeight: 500,
          },
          duration: 2.5,
        });
        setProfileForm({
          ...profileForm,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        notification.error({
          message: 'Erreur',
          description: result.message || 'Erreur lors de la mise à jour du profil',
          placement: 'topRight',
          style: {
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(255, 36, 36, 0.12)',
            background: 'linear-gradient(90deg, #ef4444 0%, #be185d 100%)',
            color: '#fff',
            fontWeight: 500,
          },
          duration: 2.5,
        });
      }
      
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      notification.error({
        message: 'Erreur',
        description: 'Erreur lors de la mise à jour du profil',
        placement: 'topRight',
        style: {
          borderRadius: 12,
          boxShadow: '0 4px 24px rgba(255, 36, 36, 0.12)',
          background: 'linear-gradient(90deg, #ef4444 0%, #be185d 100%)',
          color: '#fff',
          fontWeight: 500,
        },
        duration: 2.5,
      });
    } finally {
      setProfileLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Formulaire de profil */}
      <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
        <h4 className="text-sm sm:text-md font-medium text-gray-900 mb-3 sm:mb-4">{t('dashboard.profile.updateTitle')}</h4>
        <form onSubmit={updateProfile} className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('dashboard.profile.name')}</label>
              <input
                type="text"
                value={profileForm.name}
                onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('dashboard.profile.email')}</label>
              <input
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('dashboard.profile.username')}</label>
              <input
                type="text"
                value={profileForm.username}
                onChange={(e) => setProfileForm({...profileForm, username: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('dashboard.profile.profession')}</label>
              <input
                type="text"
                value={profileForm.profession}
                onChange={(e) => setProfileForm({...profileForm, profession: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('dashboard.profile.currentPassword')}</label>
              <input
                type="password"
                value={profileForm.currentPassword}
                onChange={(e) => setProfileForm({...profileForm, currentPassword: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('dashboard.profile.newPassword')}</label>
              <input
                type="password"
                value={profileForm.newPassword}
                onChange={(e) => setProfileForm({...profileForm, newPassword: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('dashboard.profile.confirmPassword')}</label>
              <input
                type="password"
                value={profileForm.confirmPassword}
                onChange={(e) => setProfileForm({...profileForm, confirmPassword: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            <button
              type="submit"
              className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              disabled={profileLoading}
            >
              {profileLoading ? t('dashboard.profile.updating') : t('dashboard.profile.updateBtn')}
            </button>
            <button
              type="button"
              onClick={() => setProfileForm({
                name: user?.name || '',
                email: user?.email || '',
                username: user?.username || '',
                profession: user?.profession || '',
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
              })}
              className="px-3 sm:px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
            >
              {t('dashboard.profile.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileTab; 